export type RetailChannelRole =
  | "category_channel"
  | "priority_retailer"
  | "owned_ecommerce"
  | "marketplace"
  | "mass_retailer"
  | "specialty_retailer"
  | "home_improvement"
  | "industrial_distributor";

export type AccountRetailer = {
  retailerName: string;
  retailerDomain?: string;
  aliases?: string[];
  priorityRank: number;
  channelRole: RetailChannelRole;
  estimatedChannelImportance: "high" | "medium" | "low";
  isOwnedChannel?: boolean;
};

export type RetailEntity = {
  canonicalName: string;
  aliases: string[];
};

export type RetailProductSegment = {
  label: string;
  aliases: string[];
};

export type RetailVisibilityAccountConfig = {
  accountName: string;
  customerBrandNames: string[];
  categoryTerms: string[];
  productTerms: string[];
  priorityRetailers: AccountRetailer[];
  organicRetailerEntities?: RetailEntity[];
competitorEntities?: RetailEntity[];
productSegments?: RetailProductSegment[];
  competitorBrands?: string[]; // legacy compatibility
};

export const RETAIL_VISIBILITY_ACCOUNT_IDS = new Set<string>([
  "a0693442-7c9e-4ef2-9ea9-8ce77ffe2c3a",
]);

export function isRetailVisibilityEnabled(accountId?: string | null): boolean {
  return Boolean(accountId && RETAIL_VISIBILITY_ACCOUNT_IDS.has(accountId));
}

export const RETAIL_VISIBILITY_CONFIG: Record<string, RetailVisibilityAccountConfig> = {
  forney: {
    accountName: "Forney",
    customerBrandNames: ["Forney", "Forney Industries", "forneyind.com"],
    categoryTerms: [
      "welding supplies",
      "welding equipment",
      "welding accessories",
      "MIG welding supplies",
      "welding helmets",
      "welding wire",
      "welding gloves",
    ],
    productTerms: [
      "welding helmet",
      "welding gloves",
      "welding wire",
      "MIG welder",
      "welding accessories",
      "plasma cutting accessories",
    ],
    priorityRetailers: [
  {
    retailerName: "Home Depot",
    retailerDomain: "homedepot.com",
    aliases: ["Home Depot", "HomeDepot.com", "homedepot.com"],
    priorityRank: 1,
    channelRole: "home_improvement",
    estimatedChannelImportance: "high",
  },
  {
    retailerName: "Lowe's",
    retailerDomain: "lowes.com",
    aliases: ["Lowe's", "Lowes", "lowes.com"],
    priorityRank: 2,
    channelRole: "home_improvement",
    estimatedChannelImportance: "high",
  },
  {
    retailerName: "Zoro",
    retailerDomain: "zoro.com",
    aliases: ["Zoro", "zoro.com"],
    priorityRank: 3,
    channelRole: "industrial_distributor",
    estimatedChannelImportance: "high",
  },
  {
    retailerName: "Amazon",
    retailerDomain: "amazon.com",
    aliases: ["Amazon", "amazon.com"],
    priorityRank: 4,
    channelRole: "marketplace",
    estimatedChannelImportance: "medium",
  },
  {
    retailerName: "Forney",
    retailerDomain: "forneyind.com",
    aliases: ["Forney", "Forney Industries", "forneyind.com"],
    priorityRank: 5,
    channelRole: "owned_ecommerce",
    estimatedChannelImportance: "medium",
    isOwnedChannel: true,
  },
],
organicRetailerEntities: [
  {
    canonicalName: "Cyberweld",
    aliases: ["Cyberweld", "cyberweld.com"],
  },
  {
    canonicalName: "Airgas",
    aliases: ["Airgas", "AirGasStore.com", "airgas.com"],
  },
  {
    canonicalName: "Harbor Freight",
    aliases: ["Harbor Freight"],
  },
  {
    canonicalName: "WeldingSupply.com",
    aliases: ["WeldingSupply.com", "Welding Supply", "weldingsupply.com"],
  },
  {
    canonicalName: "Northern Tool",
    aliases: ["Northern Tool", "Northern Tool + Equipment", "northerntool.com"],
  },
  {
    canonicalName: "Welding Supplies from IOC",
    aliases: [
      "Welding Supplies from IOC",
      "weldingsuppliesfromioc.com",
      "IOC",
    ],
  },
  {
    canonicalName: "Weldfabulous",
    aliases: ["Weldfabulous", "weldfabulous.com"],
  },
  {
    canonicalName: "Tractor Supply",
    aliases: ["Tractor Supply", "Tractor Supply Co.", "tractorsupply.com"],
  },
  {
    canonicalName: "Grainger",
    aliases: ["Grainger", "grainger.com"],
  },
  {
    canonicalName: "Baker's Gas",
    aliases: ["Baker's Gas", "Baker's Gas & Welding", "bakersgas.com"],
  },
],

competitorEntities: [
  {
    canonicalName: "Lincoln Electric",
    aliases: ["Lincoln Electric", "Lincoln", "lincolnelectric.com"],
  },
  {
    canonicalName: "Miller Electric",
    aliases: ["Miller Electric", "Miller", "Miller Welds", "millerwelds.com"],
  },
  {
    canonicalName: "Hobart",
    aliases: ["Hobart", "Hobart Welders"],
  },
  {
    canonicalName: "ESAB",
    aliases: ["ESAB", "Esab"],
  },
  {
    canonicalName: "Tillman",
    aliases: ["Tillman"],
  },
  {
    canonicalName: "3M Speedglas",
    aliases: ["3M Speedglas", "Speedglas", "3M"],
  },
  {
    canonicalName: "Black Stallion",
    aliases: ["Black Stallion", "Revco Black Stallion"],
  },
  {
    canonicalName: "Jackson Safety",
    aliases: ["Jackson Safety"],
  },
  {
    canonicalName: "Blue Demon",
    aliases: ["Blue Demon"],
  },
  {
    canonicalName: "Hypertherm",
    aliases: ["Hypertherm"],
  },
  {
    canonicalName: "YesWelder",
    aliases: ["YesWelder", "YESWELDER"],
  },
  {
    canonicalName: "Lotos",
    aliases: ["Lotos"],
  },
  {
    canonicalName: "Caiman",
    aliases: ["Caiman"],
  },
],

productSegments: [
  {
    label: "Welding wire / consumables",
    aliases: ["welding wire", "MIG wire", "flux-core", "consumables"],
  },
  {
    label: "MIG welders / equipment",
    aliases: ["MIG welder", "MIG welders", "welding equipment", "welder"],
  },
  {
    label: "PPE / safety gear",
    aliases: ["welding helmet", "welding helmets", "welding gloves", "safety gear"],
  },
  {
    label: "Accessories",
    aliases: ["welding accessories", "plasma cutting accessories", "cutting guide"],
  },
],
    competitorBrands: [
      "Lincoln Electric",
      "Hobart",
      "Miller",
      "ESAB",
      "Eastwood",
      "YesWelder",
      "Vulcan",
      "Titanium",
    ],
  },

  vitalProteins: {
    accountName: "Vital Proteins",
    customerBrandNames: ["Vital Proteins"],
    categoryTerms: [
      "collagen peptides",
      "collagen powder",
      "protein supplements",
      "collagen supplements",
      "beauty supplements",
      "joint health supplements",
    ],
    productTerms: [
      "collagen peptides",
      "unflavored collagen powder",
      "collagen protein powder",
      "collagen supplement",
      "beauty collagen",
    ],
    priorityRetailers: [
      {
        retailerName: "Amazon",
        retailerDomain: "amazon.com",
        priorityRank: 1,
        channelRole: "marketplace",
        estimatedChannelImportance: "high",
      },
      {
        retailerName: "Target",
        retailerDomain: "target.com",
        priorityRank: 2,
        channelRole: "mass_retailer",
        estimatedChannelImportance: "high",
      },
      {
        retailerName: "Walmart",
        retailerDomain: "walmart.com",
        priorityRank: 3,
        channelRole: "mass_retailer",
        estimatedChannelImportance: "high",
      },
      {
        retailerName: "Vital Proteins",
        retailerDomain: "vitalproteins.com",
        priorityRank: 4,
        channelRole: "owned_ecommerce",
        estimatedChannelImportance: "medium",
        isOwnedChannel: true,
      },
      {
        retailerName: "GNC",
        retailerDomain: "gnc.com",
        priorityRank: 5,
        channelRole: "specialty_retailer",
        estimatedChannelImportance: "low",
      },
      {
        retailerName: "The Vitamin Shoppe",
        retailerDomain: "vitaminshoppe.com",
        priorityRank: 6,
        channelRole: "specialty_retailer",
        estimatedChannelImportance: "low",
      },
    ],
    competitorBrands: [
      "Orgain",
      "Ancient Nutrition",
      "Sports Research",
      "Garden of Life",
      "NeoCell",
      "Great Lakes Wellness",
      "Further Food",
    ],
  },
  
};